import { ref, computed, watch } from 'vue';
import { useToast, POSITION } from 'vue-toastification';
import { adminService } from '@/services/adminService';
import { subscriptionService } from '@/services/subscriptionService';
import { calculateDaysDifference, getTomorrowDateString } from '@/utils/dateHelpers';
import type { SubscriptionPlan } from '@/services/subscriptionService';
import type { 
  RestaurantForm, 
  CreatedRestaurant, 
  RestaurantCreationData,
  PlanType,
  TrialOption
} from '@/components/admin/createRestaurantTypes';

const toast = useToast();

export function useCreateRestaurant() {
  // State
  const submitting = ref(false);
  const createdRestaurant = ref<CreatedRestaurant | null>(null);
  const restaurantCreationData = ref<RestaurantCreationData | null>(null);
  const selectedTrialOption = ref<TrialOption>(14);
  const customTrialDate = ref('');
  const availablePlans = ref<SubscriptionPlan[]>([]);
  const loadingPlans = ref(false);
  const selectedPlanType = ref<PlanType>('trial');

  // Form
  const form = ref<RestaurantForm>({
    name: '',
    subdomain: '',
    business_type: 'restaurant',
    email: '',
    admin_email: '',
    phone: '',
    address: '',
    description: '',
    trial_days: 14,
    plan_id: null
  });

  // Computed
  const minDate = computed(() => getTomorrowDateString());

  // Watchers
  watch(selectedTrialOption, (newVal) => {
    if (newVal === 'custom') {
      const today = new Date();
      today.setDate(today.getDate() + form.value.trial_days);
      customTrialDate.value = today.toISOString().split('T')[0];
    } else {
      form.value.trial_days = newVal as number;
    }
  });

  watch(customTrialDate, (newDate) => {
    if (selectedTrialOption.value === 'custom' && newDate) {
      form.value.trial_days = calculateDaysDifference(newDate);
    }
  });

  watch(selectedPlanType, (newType) => {
    if (newType === 'trial') {
      form.value.plan_id = null;
    }
  });

  // Methods
  const validateSubdomain = (event: Event) => {
    const input = event.target as HTMLInputElement;
    // Only allow lowercase letters, numbers, and hyphens
    input.value = input.value.toLowerCase().replace(/[^a-z0-9-]/g, '');
    form.value.subdomain = input.value;
  };

  const resetForm = () => {
    form.value = {
      name: '',
      subdomain: '',
      business_type: 'restaurant',
      email: '',
      admin_email: '',
      phone: '',
      address: '',
      description: '',
      trial_days: 14,
      plan_id: null
    };
    selectedTrialOption.value = 14;
    customTrialDate.value = '';
    selectedPlanType.value = 'trial';
    createdRestaurant.value = null;
    restaurantCreationData.value = null;
  };

  const loadPlans = async () => {
    loadingPlans.value = true;
    try {
      const plans = await subscriptionService.getPlans();
      // Backend already filters out trial plans (include_trial=False)
      availablePlans.value = plans;
    } catch (error: any) {
      toast.error(`Error al cargar planes: ${error.response?.data?.detail || error.message}`, {
        position: POSITION.TOP_RIGHT,
        timeout: 5000
      });
    } finally {
      loadingPlans.value = false;
    }
  };

  const handleSubmit = async () => {
    submitting.value = true;
    try {
      const response = await adminService.createRestaurant(form.value);
      
      // Check if response has welcome message (new format)
      if (response.welcome_message && response.shareable_message && response.admin_password) {
        // New format with complete welcome data
        restaurantCreationData.value = response;
        createdRestaurant.value = response.restaurant;
      } else {
        // Old format (just restaurant object) or missing data
        createdRestaurant.value = response.restaurant || response;
        restaurantCreationData.value = null;
      }
      
      toast.success('Restaurante creado exitosamente', {
        position: POSITION.TOP_RIGHT,
        timeout: 5000
      });
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Error al crear restaurante';
      toast.error(errorMessage, {
        position: POSITION.TOP_RIGHT,
        timeout: 5000
      });
    } finally {
      submitting.value = false;
    }
  };

  const getSubdomainUrl = () => {
    if (!createdRestaurant.value) return '';
    const subdomain = createdRestaurant.value.subdomain;
    // Use current hostname for development, or shopacoffee.com for production
    const hostname = window.location.hostname;
    if (hostname === 'localhost' || hostname.startsWith('127.')) {
      return `http://${subdomain}.shopacoffee.local:3000`;
    }
    return `https://${subdomain}.shopacoffee.com`;
  };

  const copyToClipboard = async (text: string, label: string = 'Texto') => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copiado al portapapeles`, {
        position: POSITION.TOP_RIGHT,
        timeout: 2000
      });
    } catch (error) {
      toast.error('Error al copiar', {
        position: POSITION.TOP_RIGHT,
        timeout: 2000
      });
    }
  };

  const copyWelcomeMessage = async () => {
    if (!restaurantCreationData.value) return;
    
    // Use the welcome_message from backend (already personalized)
    const message = restaurantCreationData.value.welcome_message;

    await copyToClipboard(message, 'Mensaje completo');
  };

  const formatTrialDate = (dateStr: string) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-ES', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  return {
    // State
    submitting,
    createdRestaurant,
    restaurantCreationData,
    selectedTrialOption,
    customTrialDate,
    availablePlans,
    loadingPlans,
    selectedPlanType,
    form,
    minDate,
    
    // Methods
    validateSubdomain,
    resetForm,
    loadPlans,
    handleSubmit,
    getSubdomainUrl,
    copyToClipboard,
    copyWelcomeMessage,
    formatTrialDate
  };
}
